import type { LoginParamsInterface, EditAccountParamsInterface, ShowParamsInterface, TwoFactorAuthResponseInterface, SessionCheckResponseInterface } from '@/interfaces/users';
import type { IManager } from '@/interfaces/hrManager';
import type { IPermissionType } from '@/interfaces/permissionTypes';
import type { IBusinessUnit } from '@/interfaces/business_unit';

import UsersResource from '@/api/resources/users';

import { isManagerContactProfileComplete } from '@/composables/useManagerContactProfile';

import { i18nGlobalInstance } from '@/plugins/i18n';
import { defineStore } from 'pinia';
import { useLoaderStore } from './loader';
import { useSessionStore } from './session';
import { useToasterStore } from '@/stores/toaster';

interface UserStateInterface {
  user?: IManager;
  permissions?: IPermissionType[];
  businessUnits?: IBusinessUnit[];
  useOnlyVirtualCards?: boolean;
}

export const useUserStore = defineStore({
  id: 'user',
  persist: true,
  state: (): UserStateInterface => ({
    user: undefined,
    permissions: [],
    businessUnits: [],
    useOnlyVirtualCards: false,
  }),
  getters: {
    currentUser: (state) => state.user,
    hasBusinessDetails: (state) => !!state?.user?.business_units?.[0],
    getBusinessDetails: (state) => state?.user?.business_units[0],
    getPermissions: (state) => state.permissions,
    getBusinessUnits: (state) => state.businessUnits,
    hasBusinessUnits: (state) => {
      if (state.businessUnits) {
        return state.businessUnits.length > 0;
      }

      return false;
    },
    isRoot: (state) => !!state.user?.is_root,
    profileContactIncomplete: (state) => {
      const user = state.user;
      if (!user?.id || user.id === 0) {
        return false;
      }
      return !isManagerContactProfileComplete(user.document, user.phone_number);
    },
  },
  actions: {
    twoFactorAuthLogin(params: LoginParamsInterface, callback: () => void) {
      const loaderStore = useLoaderStore();
      const session = useSessionStore();
      loaderStore.startLoading(true);

      UsersResource.loginV2(params)
        .then((response: TwoFactorAuthResponseInterface) => {
          this.initUser();
          this.user!.email = response.email;

          session.createTemporarySession({
            email: response.email,
            temporaryToken: response.temporary_auth_token,
            validationTokenSentAt: new Date(response.validation_token_sent_at),
          });

          callback();
        })
        .finally(() => loaderStore.stopLoading());
    },
    twoFactorAuthValidateToken(token: string, errorCallback: (error: string) => void) {
      const session = useSessionStore();
      const loaderStore = useLoaderStore();
      loaderStore.startLoading(true);

      UsersResource.validateToken({
        email: this.user!.email,
        token,
      })
        .then((user) => {
          this.$patch({
            user: {
              id: user.id,
              email: user.email,
              name: user.name,
              is_root: user.is_root,
              status: this.user?.status ?? '',
              document: user.document,
              phone_number: user.phone_number,
              should_refresh_token: user.should_refresh_token ?? false,
              using_only_virtual_card: user.using_only_virtual_card ?? false,
              token: user.token,
            } as IManager,
            permissions: user.permissions,
            businessUnits: user.business_units,
            useOnlyVirtualCards: user.using_only_virtual_card ?? false,
          });

          session.createSession({
            email: user.email,
            token: user.token,
            temporaryToken: '',
            validationTokenSentAt: null,
          });
        })
        .catch((error: string) => {
          errorCallback(error);
        })
        .finally(() => loaderStore.stopLoading());
    },
    resendToken() {
      const session = useSessionStore();
      UsersResource.resendToken().then((response) => {
        session.setValidationTokenSentAt(response.validation_token_sent_at);
      });
    },
    login(params: LoginParamsInterface) {
      const session = useSessionStore();
      const loaderStore = useLoaderStore();
      loaderStore.startLoading(true);
      UsersResource.login(params)
        .then((user) => {
          this.$patch({
            user: {
              email: user.email,
              name: user.name,
              is_root: user.is_root,
              id: user.id,
              status: '',
              document: user.document,
              phone_number: user.phone_number,
              should_refresh_token: user.should_refresh_token ?? false,
              using_only_virtual_card: user.using_only_virtual_card ?? false,
              token: user.token,
            } as IManager,
            permissions: user.permissions,
            businessUnits: user.business_units,
            useOnlyVirtualCards: user.using_only_virtual_card ?? false,
          });
          session.createSession({
            email: user.email,
            token: user.token,
            temporaryToken: '',
            validationTokenSentAt: null,
          });
        })
        .finally(() => loaderStore.stopLoading());
    },
    async refreshUser(params?: ShowParamsInterface & { silent?: boolean }) {
      const session = useSessionStore();
      const loaderStore = useLoaderStore();
      loaderStore.startLoading(true);
      
      const { silent, ...apiParams } = params || {};
      
      try {
        let user = silent 
          ? await UsersResource.showSilent(apiParams)
          : await UsersResource.show(apiParams);
        
        this.user = user;
        this.permissions = user.permissions;
        this.businessUnits = user.business_units;
        this.useOnlyVirtualCards = user.using_only_virtual_card ?? false;

        if(user.should_refresh_token) {
          user = await this.refreshUser({ refresh_token: true, silent });
        }

        if (user.token && user.email && !session.isHttpOnlySession) {
          session.createSession({
            email: user.email,
            token: user.token,
            temporaryToken: '',
            validationTokenSentAt: null,
          });
        }
        
        return user;
      } finally {
        loaderStore.stopLoading();
      }
    },
    fetchPermissions() {
      const loaderStore = useLoaderStore();
      loaderStore.startLoading(true);
      UsersResource.fetchPermissions()
        .then((permissions) => {
          this.$patch({ permissions });
        })
        .finally(() => loaderStore.stopLoading());
    },
    update(params: EditAccountParamsInterface, callback: () => void) {
      const session = useSessionStore();
      const loaderStore = useLoaderStore();
      const { addMessage } = useToasterStore();
      loaderStore.startLoading(true);
      UsersResource.update(params)
        .then((user) => {
          addMessage({ text: i18nGlobalInstance.t('success.operation') });
          this.$patch({
            user: {
              ...this.user,
              id: user.id ?? this.user?.id,
              email: user.email,
              name: user.name,
              is_root: user.is_root ?? this.user?.is_root,
              status: this.user?.status ?? '',
              document: user.document,
              phone_number: user.phone_number,
              should_refresh_token: user.should_refresh_token ?? false,
              using_only_virtual_card: user.using_only_virtual_card ?? false,
              token: user.token ?? this.user?.token,
            } as IManager,
            permissions: user.permissions,
            businessUnits: user.business_units,
          });
          if ((user.email || user.token) && !session.isHttpOnlySession) {
            session.createSession({
              email: user.email,
              token: session.token,
              temporaryToken: '',
              validationTokenSentAt: null,
            });
          }
          callback();
        })
        .catch(() => {
          addMessage({ text: i18nGlobalInstance.t('errors.default_error') });
        })
        .finally(() => loaderStore.stopLoading());
    },
    async logout() {
      const session = useSessionStore();
      const { addMessage } = useToasterStore();

      try {
        await UsersResource.signOut();
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('[user.logout] sign_out request failed', error);
        }
        addMessage({ text: i18nGlobalInstance.t('errors.logout_failed') });
      }

      this.$reset();
      session.destroySession();
    },
    async checkSession(): Promise<SessionCheckResponseInterface | null> {
      try {
        const response = await UsersResource.checkSession();
        
        if (response.authenticated && response.user) {
          const session = useSessionStore();
          
          this.$patch({
            user: {
              id: response.user.id,
              email: response.user.email,
              name: response.user.name,
              is_root: response.user.is_root,
              document: response.user.document,
              phone_number: response.user.phone_number,
            } as IManager,
          });
          
          if (response.auth_method === 'cookie') {
            session.createHttpOnlySession(response.user.email);
          }
          
          return response;
        }
        
        return null;
      } catch {
        return null;
      }
    },
    initUser() {
      this.user = {
        id: 0,
        email: '',
        name: '',
        status: '',
        is_root: false,
        permissions: [],
        business_units: [],
        should_refresh_token: false,
        using_only_virtual_card: false,
        token: '',
        document: '',
        phone_number: '',
      };
    },
  },
});
